import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { ColumnDef } from '@tanstack/react-table';
import { LunchRequest } from '../../types/manage-lunch.types';

interface CreateFoodRequestsTableColumnsProps {
  t: (key: string) => string;
  /** The employee's email for the name's subtitle; requests do not carry it. */
  emailOf?: (userId: string) => string | undefined;
  /** Every department, for the Department column's dropdown filter (the list is not scoped). */
  departments?: readonly { ItemId: string; Title: string }[];
}

export const createFoodRequestsTableColumns = ({
  t,
  emailOf,
  departments = [],
}: CreateFoodRequestsTableColumnsProps): ColumnDef<LunchRequest>[] => [
  {
    id: 'DisplayName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName ?? '',
    cell: ({ row }) => (
      <UserNameCell name={row.original.DisplayName} email={emailOf?.(row.original.UserId)} />
    ),
  },
  {
    id: 'DepartmentName',
    // Card: top right, beside the employee's name. Filtered by the department name stored on the
    // request.
    meta: {
      mobile: 'badge',
      filter: {
        type: 'options',
        dropdown: true,
        options: departments.map((department) => ({
          value: department.Title,
          label: department.Title,
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '-',
  },
  {
    id: 'MenuName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('MENU')} />,
    accessorFn: (row) => row.MenuName ?? '-',
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <span className="font-medium text-foreground">{row.original.MenuName ?? '-'}</span>
        {row.original.MenuItems && row.original.MenuItems.length > 0 && (
          <span className="truncate text-xs text-muted-foreground">
            {row.original.MenuItems.join(', ')}
          </span>
        )}
      </div>
    ),
  },
  {
    id: 'CreatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REQUESTED_ON')} />,
    accessorFn: (row) => row.CreatedDate ?? '',
    cell: ({ row }) =>
      row.original.CreatedDate ? formatOrgDateTime(row.original.CreatedDate) : '-',
  },
];
